import { useState, useRef, useCallback } from 'react';
import { Button } from '@/components/ui/button';

type MessageInputProps = {
  onSend: (input: string) => void;
  disabled: boolean;
};

const MAX_HEIGHT = 120;

function useAutoResize() {
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const resize = useCallback(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, MAX_HEIGHT)}px`;
  }, []);

  const reset = useCallback(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
  }, []);

  return { textareaRef, resize, reset };
}

export function MessageInput({ onSend, disabled }: MessageInputProps) {
  const [value, setValue] = useState('');
  const { textareaRef, resize, reset } = useAutoResize();

  const handleSend = useCallback(() => {
    const trimmed = value.trim();
    if (!trimmed) return;
    onSend(trimmed);
    setValue('');
    reset();
  }, [value, onSend, reset]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleSend();
      }
    },
    [handleSend]
  );

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      setValue(e.target.value);
      resize();
    },
    [resize]
  );

  return (
    <div className="flex items-end gap-2 border-t p-3">
      <textarea
        ref={textareaRef}
        value={value}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        disabled={disabled}
        placeholder="Type a message..."
        rows={1}
        className="border-input bg-background min-h-[36px] flex-1 resize-none rounded-md border px-3 py-2 text-sm outline-none transition-colors focus-visible:border-ring disabled:cursor-not-allowed disabled:opacity-50"
        style={{ maxHeight: `${MAX_HEIGHT}px` }}
      />
      <Button onClick={handleSend} disabled={disabled || !value.trim()} size="sm">
        Send
      </Button>
    </div>
  );
}
